import CustomDialog from '@components/dialog/CustomDialog';
import Iconify from '@components/iconify';
import { Button, Stack, Typography } from '@mui/material';
import icons from '@utils/icons';
import { useTranslation } from 'react-i18next';
import { BooleanUtil, useBoolean } from 'src/hooks/use-boolean';

export interface IUnsavedWarningModal {
  navigateCta?: VoidFunction;
  title?: string;
  description?: string;
  cancelLabel?: string;
  cancelCta?: () => Promise<void>;
  cancelShouldNavigate?: boolean;
  confirmLabel?: string;
  confirmCta?: () => Promise<void>;
  confirmShouldNavigate?: boolean;
}
interface Props {
  handeler: BooleanUtil;
  modalData: IUnsavedWarningModal;
}

export default function UnsavedWarningModal({ handeler, modalData }: Props) {
  const { t } = useTranslation('commons', { keyPrefix: 'unsaved-content-dialog' });
  const confirmLoading = useBoolean();
  const cancelLoading = useBoolean();

  const {
    navigateCta = () => {},
    title = t('title'),
    description = t('description'),
    cancelLabel = t('cancel'),
    cancelCta = (): Promise<void> => Promise.resolve(),
    cancelShouldNavigate = false,
    confirmLabel = t('confirm'),
    confirmCta = (): Promise<void> => Promise.resolve(),
    confirmShouldNavigate = true,
  } = modalData;

  const handleCancel = async () => {
    cancelLoading.onTrue();
    try {
      await cancelCta();
      handeler.onFalse();
      if (cancelShouldNavigate) navigateCta();
    } catch (e) {
      console.error(e);
    }
    cancelLoading.onFalse();
  };

  const handleConfirm = async () => {
    confirmLoading.onTrue();
    try {
      await confirmCta();
      handeler.onFalse();
      if (confirmShouldNavigate) navigateCta();
    } catch (e) {
      console.error(e);
    }
    confirmLoading.onFalse();
  };

  return (
    <CustomDialog
      maxWidth="xs"
      breakContent
      open={handeler.value}
      onClose={handeler.onFalse}
      title={title}
      hideDivider
      body={
        <Stack spacing={3}>
          <Typography textAlign="start">{description}</Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCancel}
              startIcon={cancelLoading.value && <Iconify icon={icons.commons.loading} />}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleConfirm}
              startIcon={confirmLoading.value && <Iconify icon={icons.commons.loading} />}
            >
              {confirmLabel}
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
}
